/**
 * LandingPricing — the public pricing section (TRIAL_PLAN.md §8.1, Phase 9).
 *
 * Every number here is READ, never written: the membership fee and silo caps
 * come from /public/pricing, the Pro prices and the bundle come from the live
 * Creator Pro catalog. A hardcoded price on a landing page is how a retired
 * price survived three months past the change — see lib/usePublicPricing.js.
 *
 * Deliberately not a three-column "choose your plan" grid: there is one
 * subscription and everything else is an optional one-time upgrade. Saying that
 * plainly reads better than inventing tiers.
 *
 * `dark` follows the landing page's own theme switch (sunrise photo vs paper).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Lightning, Stack, Gauge } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";
import WhatIsASilo from "@/components/WhatIsASilo";

const CREATOR_INCLUDES = [
  "Your silos, trained only on what you upload",
  // Oct 2026 (Randy) — this used to promise "stage, condense, compare, commit".
  // Condense and compare are gated behind min_workbench, so a $15 creator never
  // had them: *"the marketing on the home page cannot promote this under the
  // $15 creator access features."* What they do have is a place to write.
  "The Recursive Workbench — write, push it straight into your silo",
  "Tone Architect, so answers sound like you wrote them",
  "A public page with Q&A, fan subscriptions and tips",
  "Probe, Ops and Users — see what's asked, what it costs, who's asking",
];

// Randy's words for each suite, Oct 2026. Keyed off the catalogue so a renamed
// suite falls back to a plain count rather than claiming the wrong thing.
const SUITE_TAGLINE = {
  workbench: "tools you'll want",
  extras: "tools you'll love",
};
const PAPER = {
  eyebrow: "text-xs uppercase tracking-[0.25em] text-[#6B6A68] mb-3",
  h2: "font-serif-display text-4xl sm:text-5xl text-[#1C1B1A] max-w-3xl leading-tight",
  accent: "not-italic text-[#4A5D4E]",
  card: "rounded-2xl border hairline bg-white/70 p-8",
  cardSm: "rounded-2xl border hairline bg-white/70 p-7",
  note: "rounded-2xl border hairline bg-[#F4F1E8]/70 p-7",
  label: "flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#6B6A68]",
  muted: "text-[#6B6A68]",
  check: "text-[#4A5D4E]",
  cta: "mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#1C1B1A] text-[#FDFCF8] text-sm hover:bg-[#3A4A3E] transition-colors",
  bundle: "rounded-2xl border border-[#4A5D4E]/25 bg-[#1C1B1A] text-[#FDFCF8] p-7",
  bundleMuted: "text-[#FDFCF8]/70",
  strike: "text-[#FDFCF8]/50 line-through",
};

const SUNRISE = {
  eyebrow: "text-xs uppercase tracking-[0.3em] text-[#FFC24A] mb-3 [text-shadow:0_1px_12px_rgba(0,0,0,0.95)]",
  h2: "font-serif-display text-4xl sm:text-5xl text-[#F5F1E8] max-w-3xl leading-tight [text-shadow:0_2px_30px_rgba(0,0,0,0.85)]",
  accent: "not-italic text-[#FFC24A] font-normal",
  card: "rounded-2xl p-8 border border-[#F5F1E8]/12 bg-[#070B11]/25 backdrop-blur-2xl backdrop-brightness-[0.38] backdrop-saturate-150 text-[#F5F1E8] shadow-[0_18px_50px_-20px_rgba(0,0,0,0.75)]",
  cardSm: "rounded-2xl p-7 border border-[#F5F1E8]/12 bg-[#070B11]/25 backdrop-blur-2xl backdrop-brightness-[0.38] backdrop-saturate-150 text-[#F5F1E8] shadow-[0_18px_50px_-20px_rgba(0,0,0,0.75)]",
  note: "rounded-2xl p-7 border border-[#FFC24A]/18 bg-[#05080C]/30 backdrop-blur-2xl backdrop-brightness-[0.34] backdrop-saturate-150 text-[#F5F1E8]",
  label: "flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#FFC24A]/75",
  muted: "text-[#E7E2D6]/70",
  check: "text-[#FFC24A]",
  cta: "mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#F5EFE2] text-[#14110C] text-sm hover:bg-[#FFC24A] transition-colors shadow-[0_10px_40px_-8px_rgba(255,194,74,0.5)]",
  bundle: "rounded-2xl p-7 border border-[#FFC24A]/35 bg-[#05080C]/35 backdrop-blur-2xl backdrop-brightness-[0.30] backdrop-saturate-150 text-[#F5F1E8] shadow-[0_18px_50px_-20px_rgba(0,0,0,0.85)]",
  bundleMuted: "text-[#F5F1E8]/85",
  strike: "text-[#E7E2D6]/45 line-through",
};

export default function LandingPricing({ dark = false, audience = "anyone", intro = "" }) {
  const S = dark ? SUNRISE : PAPER;
  const pricing = usePublicPricing();
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    api.get("/creator-pro/catalog")
      .then((r) => setCatalog(r.data?.catalog || r.data))
      .catch(() => setCatalog(null));
  }, []);

  const suites = catalog?.suites || [];
  const items = suites.flatMap((s) => s.items || []);
  // Oct 2026 (HOMEPAGE_PRICING_REVAMP.md §2) — count the WHOLE menu, children
  // included. Summing top-level items only put the à-la-carte total at $465
  // when the real figure is $653: the seven nested add-ons (Macro Auto-build,
  // AI Comment Replies, Response Engine, the two knowledge extensions, Refinery
  // Boost, AI Calendar) are $188 of buyable tools the page was hiding, which
  // understated the bundle's saving by the same amount.
  const alaCarteCents = items.reduce(
    (n, i) => n + (i.day1_cents || 0)
      + (i.children || []).reduce((m, c) => m + (c.day1_cents || 0), 0),
    0,
  );
  const bundleCents = catalog?.bundle?.day1_cents ?? pricing.day_one_bundle_discount_threshold_cents;
  const saveCents = alaCarteCents && bundleCents ? alaCarteCents - bundleCents : null;
  const fee = pricing.creator_monthly_fee_cents ?? pricing.platform_fee_cents;

  return (
    <section className="max-w-6xl mx-auto px-6 pb-28" data-testid="landing-pricing">
      <div className={S.eyebrow}>Pricing</div>
      <h2 className={S.h2}>
        One subscription. <em className={S.accent}>Everything else is optional.</em>
      </h2>
      <div className="mt-3">
        <WhatIsASilo dark={dark} variant="pricing" testid="pricing-what-is-a-silo" />
      </div>
      {/* Oct 2026 — the plans and the numbers are identical for every audience
          (Randy: same prices, audience framing). Only this line moves. */}
      {intro && (
        <p className={`mt-4 text-sm max-w-2xl ${S.muted}`} data-testid={`landing-pricing-intro-${audience}`}>
          {intro}
        </p>
      )}

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
        <div className={`lg:col-span-3 ${S.card}`} data-testid="landing-pricing-creator">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-serif-display text-3xl">Creator</span>
            <span className="text-2xl font-medium" data-testid="landing-pricing-creator-price">{fmt$(fee)}</span>
            <span className={`text-sm ${S.muted}`}>/month</span>
          </div>
          <p className={`mt-2 text-sm ${S.muted}`}>
            Up to {pricing.creator_tier_silo_cap} silos. Cancel from Account → Billing in one click.
          </p>
          <ul className="mt-6 space-y-2.5">
            {CREATOR_INCLUDES.map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-sm">
                <Check size={15} weight="bold" className={`${S.check} mt-0.5 shrink-0`} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <Link to="/signup?as=trial" data-testid="landing-pricing-cta" className={S.cta}>
            Start 14 days free <ArrowRight size={15} />
          </Link>
          <p className={`mt-3 text-xs ${S.muted}`} data-testid="landing-pricing-trial-terms">
            Card required. {fmt$(fee)} begins on day 15 — cancel any time before then and you are
            charged nothing.
          </p>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className={S.cardSm} data-testid="landing-pricing-pro">
            <div className={S.label}><Stack size={14} weight="duotone" /> Pro upgrades</div>
            <div className="font-serif-display text-2xl mt-2">One-time purchase. Massive Data tools!</div>
            <p className={`mt-2 text-sm ${S.muted}`}>
              Upgrade what you need. No recurring charge, become a Creator Pro. Eligible for the
              My Silo Partner Program. They lift your cap to {pricing.creator_pro_tier_silo_cap} silos.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {suites.map((s) => {
                const count = (s.items || []).length;
                const tagline = SUITE_TAGLINE[s.key];
                return (
                  <li key={s.key} className="flex items-baseline justify-between gap-3">
                    <span>{s.name}</span>
                    <span className={`text-xs ${S.muted}`}>
                      {count} {tagline || "tools"}
                    </span>
                  </li>
                );
              })}
              {!suites.length && <li className={`text-xs ${S.muted}`}>Loading the menu…</li>}
            </ul>
          </div>

          <div className={S.bundle} data-testid="landing-pricing-bundle">
            <div className={S.label}>
              <Lightning size={14} weight="duotone" /> {catalog?.bundle?.name || "Creator Pro"}
            </div>
            <div className="flex items-baseline gap-3 mt-2">
              <span className="font-serif-display text-3xl" data-testid="landing-pricing-bundle-price">
                {fmt$(bundleCents)}
              </span>
              {alaCarteCents ? <span className={`text-sm ${S.strike}`}>{fmt$(alaCarteCents)}</span> : null}
            </div>
            <p className={`mt-2 text-sm ${S.bundleMuted}`}>
              Every Pro tool at once{saveCents ? <> — {fmt$(saveCents)} less than buying them à la
              carte</> : null}. One payment, no subscription.
            </p>
          </div>
        </div>
      </div>

      <div className={`mt-8 ${S.note}`} data-testid="landing-pricing-funding">
        <div className={S.label}><Gauge size={14} weight="duotone" /> Plus your own AI funding</div>
        <p className="mt-2 text-sm leading-relaxed max-w-3xl">
          AI answers cost money, but My Silo is built to keep that cost extremely low. We've put
          serious work into efficiently querying even huge collections of knowledge. And unlike
          platforms that mark up AI usage, My Silo can connect you <strong>directly to the
          source</strong> with your own provider key. We'll even help you set it up. With efficient
          default models, many queries cost only pennies or less, while more powerful models are
          always there when you want to turn things up.
        </p>
      </div>
    </section>
  );
}
